import { useTranslation } from 'react-i18next'

import { Skeleton } from '@/components/feedback/Skeleton'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import {
  BlockAddRow,
  BlockDeskError,
  BlockLine,
  BlockListEmpty,
} from '@/features/quotes/components/QuoteBlockDesk'
import { useBlockDesk } from '@/features/quotes/components/useBlockDesk'
import type { QuoteDetail } from '@/types/domain'

/* IMPORTANT NOTES — the third block of the reference quotation (FR-60).
 *
 * The sentences that decide an argument: how long the load may stand before
 * demurrage starts, what standing longer costs, how the handover happens, what
 * the rate is conditional on. They print on the offer after the price, verbatim,
 * one paragraph each.
 *
 * ── A list, and only a list ─────────────────────────────────────────────────
 *
 * This card briefly carried two structured fields as well — the demurrage
 * wording and the delivery description — and they are gone from it. What a
 * reader does here is read four sentences and change one, so the card is four
 * sentences and the row that adds a fifth. Both fields are still on the quote
 * and still printed by the composer; the builder's pricing step is where they
 * are answered.
 *
 * THE CONSEQUENCE IS WORTH KNOWING: `offer.go` prints those two as labelled
 * terms of their own AND prints every note here. A note that restates the
 * demurrage terms therefore reaches the client twice, once as "Demurrage: USD
 * 80/day" and once in its own words. Making the notes the single source is a
 * change to the composer and to what clients receive, so it has not been made
 * here.
 *
 * ── Why the notes are the `disclaimer` kind ─────────────────────────────────
 *
 * Because they already were. The wire word is `disclaimer` — spelled as
 * document_templates spells it (0032), so a copy is a copy and not a
 * translation — and the reference prints the same block as IMPORTANT NOTES. The
 * screen shows the reader's word and the storage keeps the contract's; nothing
 * was added to `QuoteBlockKind`.
 *
 * A new quote opens with the four standard notes already in it (quote.Create).
 * They are ordinary rows from the first render: editable, deletable, and
 * printed exactly as they read here.
 *
 * One thing this card does NOT do: number them "Note 1, Note 2" as the paper
 * does. That column is not the block's `label` — 0033 keeps the filing handle
 * off the client's copy — and the composer prints notes today as bare
 * paragraphs with no heading above them. Making the DOCUMENT match the
 * reference is a change to `offer.go`, and a change to what clients receive.
 */
export function QuoteNotesCard({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()
  const desk = useBlockDesk(record)

  const notes = desk.of('disclaimer')

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('quotes.notes.title')}</CardTitle>
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {desk.readOnly ? t('quotes.notes.frozen') : t('quotes.notes.hint')}
        </p>
      </CardHeader>

      <CardBody className="flex flex-col gap-3 pt-2">
        <BlockDeskError desk={desk} />

        {desk.loading ? (
          <Skeleton className="h-24 w-full rounded-md" />
        ) : desk.failedToLoad ? (
          <p role="alert" className="text-negative text-[12.5px] leading-snug">
            {t('quotes.blocks.loadFailed')}
          </p>
        ) : (
          <>
            {notes.length === 0 ? (
              <BlockListEmpty>{t('quotes.notes.empty')}</BlockListEmpty>
            ) : (
              <ul className="flex flex-col">
                {notes.map((note) => (
                  <BlockLine
                    key={note.id}
                    block={note}
                    desk={desk}
                    editLabel={t('quotes.notes.edit')}
                    deleteLabel={t('quotes.notes.remove')}
                  />
                ))}
              </ul>
            )}

            {desk.readOnly ? null : (
              <BlockAddRow
                desk={desk}
                kind="disclaimer"
                placeholder={t('quotes.notes.addPlaceholder')}
                actionLabel={t('quotes.notes.add')}
              />
            )}
          </>
        )}
      </CardBody>
    </Card>
  )
}
